/**
 * Lander hit from Stripe after the save-card setup session completes.
 * Polls the backend which retrieves the SetupIntent + persists the
 * PaymentMethod, then bounces back to Marketplace settings.
 */
import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Loader2, CheckCircle2, AlertTriangle, CreditCard } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function SaveCardReturn() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const fired = useRef(false);
  const [state, setState] = useState({ loading: true, ar: null, error: null });

  useEffect(() => {
    if (fired.current) return;
    fired.current = true;
    if (!sessionId) {
      setState({ loading: false, ar: null, error: "Missing session id." });
      return;
    }
    api
      .get(`/marketplace/wallet/save-card/return?session_id=${encodeURIComponent(sessionId)}`)
      .then((r) => setState({ loading: false, ar: r.data?.auto_reload || null, error: null, status: r.data?.status }))
      .catch((e) => setState({
        loading: false,
        ar: null,
        error: formatApiErrorDetail(e.response?.data?.detail) || "Could not confirm card save",
      }));
  }, [sessionId]);

  if (state.loading) {
    return (
      <Frame>
        <div className="inline-flex items-center gap-2 text-slate-500" data-testid="save-card-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Confirming with Stripe…
        </div>
      </Frame>
    );
  }

  if (state.error) {
    return (
      <Frame>
        <div data-testid="save-card-error">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-amber-100 text-amber-600 mb-5">
            <AlertTriangle className="w-7 h-7" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-display font-bold text-slate-900 mb-2">Couldn't save your card</h1>
          <p className="text-slate-600 mb-6">{state.error}</p>
          <Button onClick={() => navigate("/settings?tab=marketplace")} className="bg-[#C75D33] hover:bg-[#A14828] text-white font-semibold">
            Back to marketplace settings
          </Button>
        </div>
      </Frame>
    );
  }

  if (state.status !== "ok") {
    return (
      <Frame>
        <div data-testid="save-card-pending">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-amber-100 text-amber-600 mb-5">
            <Loader2 className="w-7 h-7 animate-spin" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-display font-bold text-slate-900 mb-2">Still processing</h1>
          <p className="text-slate-600 mb-6">Stripe is finishing up. Give it a moment and refresh, or head back to marketplace settings.</p>
          <Button onClick={() => navigate("/settings?tab=marketplace")} className="bg-slate-900 hover:bg-slate-800 text-white font-semibold">
            Back to settings
          </Button>
        </div>
      </Frame>
    );
  }

  const ar = state.ar || {};
  return (
    <Frame>
      <div data-testid="save-card-success">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 mb-5">
          <CheckCircle2 className="w-9 h-9" strokeWidth={2.5} />
        </div>
        <h1 className="text-2xl sm:text-4xl font-display font-bold text-slate-900 mb-2">Card saved ✓</h1>
        <p className="text-slate-600 mb-6">
          {ar.card_brand ? <><span className="capitalize">{ar.card_brand}</span> ending in <span className="font-semibold">{ar.card_last4}</span> is on file.</> : "Your card is on file."} Turn on auto-reload to lock in lead delivery.
        </p>
        <div className="flex gap-3 justify-center">
          <Button asChild className="bg-[#C75D33] hover:bg-[#A14828] text-white font-semibold">
            <Link to="/settings?tab=marketplace">Marketplace settings →</Link>
          </Button>
        </div>
      </div>
    </Frame>
  );
}

function Frame({ children }) {
  return (
    <div className="min-h-screen bg-[#FBFAF6] flex items-center justify-center px-6">
      <div className="max-w-md w-full text-center">
        <div className="inline-flex items-center gap-2 mb-8 text-slate-500 text-sm">
          <CreditCard className="w-4 h-4" /> Wallet auto-reload
        </div>
        {children}
      </div>
    </div>
  );
}
